import { useCallback, useSyncExternalStore } from "react";

// Port web de `AppState` (react-native).
//
// Correspondance retenue :
//   AppState "active"      → onglet visible          (visibilityState === "visible")
//   AppState "background"  → onglet caché / minimisé (visibilityState === "hidden")
//   AppState "inactive"    → PAS D'ÉQUIVALENT. C'était l'état transitoire iOS
//                            (app switcher, centre de contrôle). Le web ne le
//                            distingue pas de "hidden". Le code natif l'ignorait
//                            déjà explicitement, donc rien n'est perdu.
//
// Usage principal côté natif : relancer une vérification quand l'app revient au
// premier plan (token watcher du RootNavigator, refetch de suivi). C'est ce que
// couvre `onForeground` ci-dessous.

export type AppStateStatus = "active" | "background";

export function getAppState(): AppStateStatus {
  return document.visibilityState === "visible" ? "active" : "background";
}

/**
 * Exécute `callback` à chaque retour au premier plan de l'onglet.
 * Renvoie la fonction de désabonnement.
 *
 * On écoute AUSSI `focus` : passer d'une autre fenêtre à celle-ci sans que
 * l'onglet ait jamais été caché ne déclenche pas `visibilitychange`, alors que
 * l'utilisateur revient bien sur l'app.
 */
export function onForeground(callback: () => void): () => void {
  const onVisibility = () => {
    if (document.visibilityState === "visible") callback();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", callback);

  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", callback);
  };
}

/** Variante hook, pour les composants qui veulent réagir au rendu. */
export function useAppState(): AppStateStatus {
  const subscribe = useCallback((onChange: () => void) => {
    document.addEventListener("visibilitychange", onChange);
    window.addEventListener("focus", onChange);
    window.addEventListener("blur", onChange);
    return () => {
      document.removeEventListener("visibilitychange", onChange);
      window.removeEventListener("focus", onChange);
      window.removeEventListener("blur", onChange);
    };
  }, []);

  return useSyncExternalStore(
    subscribe,
    getAppState,
    () => "active" as AppStateStatus,
  );
}
